import { ofType } from 'redux-observable';
import { Observable, of } from 'rxjs';
import { distinctUntilChanged, switchMap, withLatestFrom } from 'rxjs/operators';
import { isArchivedByCouple } from '@bridebook/toolbox/src/inbox/utils';
import { App } from '@capacitor/app';
import { Haptics, NotificationType } from '@capacitor/haptics';
import { AuthActionTypes } from '@bridebook/web/lib/auth/action-types';
import { getCoupleChatUserOptions } from '@bridebook/web/lib/inbox/suppliers/selectors';
import { TalkJsManager } from '@bridebook/web/lib/inbox/suppliers/talkjs';
import { Action, IApplicationState, IEpicDeps } from '@bridebook/web/lib/types';
import { IUpdateWeddingAction, WeddingActionTypes } from '@bridebook/web/lib/weddings/action-types';
import { updateInboxUnread } from '@bridebook/web/lib/weddings/actions';
import { getQueryClient } from '@bridebook/web/store/react-query-client';
import { QueryClient } from '@tanstack/react-query';
import {
  invalidateConversationsQuery,
  USE_CONVERSATIONS_QUERY_KEY,
} from '@bridebook/web/lib/inbox/hooks/use-conversations';
import { Conversation } from '@bridebook/toolbox/src/inbox/api/talkjs/webhook.types';

const doesConversationExists = (queryClient: QueryClient, conversationId: string) => {
  const conversations = queryClient.getQueryData<Conversation[]>(USE_CONVERSATIONS_QUERY_KEY);

  return conversations?.some((c) => c.id === conversationId);
};

const watchInboxUnread$ = (state: IApplicationState) => {
  const chatUserOptions = getCoupleChatUserOptions(state);
  if (!chatUserOptions) return of();

  return new Observable((observer) => {
    (async () => {
      const talkManager = await TalkJsManager();
      const talkSession = await talkManager.get(chatUserOptions);

      if (!talkSession) return of();

      // Set number of unread messages
      talkSession.unreads.onChange((unreadConversations) => {
        observer.next(updateInboxUnread(unreadConversations));
      });

      // Trigger haptic feedback on new message
      talkSession.onMessage((message) => {
        const queryClient = getQueryClient();
        const conversationExists = doesConversationExists(queryClient, message.conversation.id);
        if (!conversationExists) {
          invalidateConversationsQuery(queryClient);
        }

        App.getState().then(({ isActive }) => {
          // Only when app currently in foreground and message is not sent by user
          // We add a trivial exception handler because this method always throws in Safari:
          if (isActive && !message.isByMe && !isArchivedByCouple(message.conversation)) {
            Haptics.notification({
              type: NotificationType.Success,
            }).catch((_) => _);
          }
        });
      });
    })();
  });
};

export const initInboxListenerEpic = (
  action$: Observable<IUpdateWeddingAction>,
  { state$ }: IEpicDeps,
) =>
  action$.pipe(
    ofType<Action>(WeddingActionTypes.UPDATE_WEDDING, AuthActionTypes.USER_SETUP_COMPLETED),
    withLatestFrom(state$),
    distinctUntilChanged(
      ([prevAction], [nextAction]) =>
        prevAction.payload.wedding.id === nextAction.payload.wedding.id,
    ),
    switchMap(([, state]) => watchInboxUnread$(state)),
  );
